/**
 * Driver-facing content: the in-app Manual and the What's New changelog.
 *
 * One source, two consumers:
 *   - apps/web HelpPage renders MANUAL_PAGES + CHANGELOG_ENTRIES.
 *   - apps/api driver-assist.ts feeds EVII MANUAL_PAGES + the latest updates.
 * Edit the content here and both surfaces stay in sync.
 *
 * House style: no em or en dashes, active voice, concrete nouns,
 * one idea per sentence.
 */
import manualChunksRaw from './generated/manual-chunks.json' with { type: 'json' };
import { CONTENT_OVERLAYS } from './locales/registry.js';
import { idleManualBody } from '@evi/shared';
/**
 * The changelog, newest first. The Help page renders the full list. EVII
 * is fed only the most recent few (see driver-assist.ts) so the prompt
 * stays bounded as this grows.
 *
 * DEFAULT STYLE: the What's New card renders every entry as a bullet list,
 * one bullet per line. Author each `body` as an array of one-idea lines
 * joined with '\n'. A single-idea entry may stay a plain one-line string
 * (it renders as one bullet). The '\n' feeds EVII cleanly too.
 */
export const CHANGELOG_ENTRIES = [
    {
        // LAUNCH ENTRY, dated the flip: 20 October 2026, the day v1 drivers open
        // the new app. The date is this entry's overlay key, so moving it again
        // means moving its translation in every locale file too.
        date: '2026-10-20',
        title: 'Welcome to the new app',
        // One idea per line; the What's New card renders each line as its own
        // bullet (see the CHANGELOG_ENTRIES header note for the default style).
        body: [
            'Sign in with the same email and password as before, or with Google. Your charging '
                + 'history and wallet came with you.',
            'The map is the first screen. Your three shortcuts sit under the plus button.',
            'While you charge, the charging screen is the home screen: energy added, cost so far, '
                + 'your rate and the time, with End Session right under them.',
            'EVII is the assistant built into the app. She greets you by name, answers questions '
                + 'about your account, and offers a button to the right screen when you ask.',
            'The manual and What\'s New live under Help, in Menu > Account.',
            'Push notifications are ready to turn on in Settings > Push notifications.',
            'If you host a home charger, the app walks you from adding it to earning from it.',
        ].join('\n'),
    },
    {
        date: '2026-10-20',
        title: 'Version 3.3',
        // One idea per line; the What's New card renders each line as its own
        // bullet (see the CHANGELOG_ENTRIES header note for the default style).
        body: [
            'Some chargers now have an idle fee if your car stays plugged in after charging '
                + 'finishes. Before you tap Start charging, the app shows the idle rate, the free '
                + 'minutes and the most the idle fee can be.',
            'When charging finishes, the app counts down your free minutes so you can unplug '
                + 'before an idle fee starts.',
            'Any idle fee is charged automatically after you unplug, so there is nothing extra '
                + 'to do.',
            'If a payment does not go through, open Payment methods and pay the outstanding '
                + 'balance with a saved or new card. You need to pay it before you can start '
                + 'another charge.',
            'Your receipt shows any idle fee with its billed minutes, rate, free minutes and '
                + 'maximum. History shows the fee with its billed minutes, rate and free minutes.',
            'View receipt opens that session\'s own receipt for every finished session, including '
                + 'wallet and free sessions.',
            'If your default card has expired, the app tells you when you start and offers to '
                + 'make your new card the default.',
            'At chargers that bill in Canadian dollars you pay by card, because your wallet '
                + 'balance is in US dollars.',
            'When a host has not set a guest price, their charger\'s station page and charge '
                + 'screen say it isn\'t open to guests yet.',
            'At some newly installed chargers, the start button says Plug in here to start until '
                + 'the charger sees your car.',
            'New games while you charge: Blackout Run on the INITIATIVE theme, Surge Pack on the '
                + 'Cyber theme, and Power Lap, a 3D kart race that replaces Neon Drift on the '
                + 'Neon theme.',
            'The Cartographic, Midnight, Sunset and Bloom charging screens now respond to '
                + 'your touch.',
            'You can pick 3rd-Party Chargers, your list of chargers other people share with you, '
                + 'as one of your three shortcuts.',
            'If the map cannot load chargers, it says Locations could not be loaded and '
                + 'offers Retry.',
            'My EV shows a car that EVI support has turned off, instead of hiding it.',
            'The sign up screen links to the Terms of Service and the Privacy Policy.',
            'On Android, the app installed on your home screen stays in portrait.',
            'Emails from EV Initiative have a new light design.',
            'More of the app now shows in the language you choose, including the payment and idle '
                + 'fee screens.',
            'If your home is in Canada, guests pay your price in Canadian dollars once you save '
                + 'it, and your earnings show in Canadian dollars.',
            'Home hosts can add an idle fee of up to $2 a minute, after 5 to 30 free minutes, and '
                + 'can set a lower maximum per session.',
            'You can register your home charger before it is online, then enter the server URL, '
                + 'charger ID and password that My Home shows into the Autel app.',
            'Set up My Home asks for your country, checks your state or province and postal code, '
                + 'and sets your home\'s time zone from this phone or your address.',
            'Before payout setup you choose whether your bank account is in the United States or '
                + 'Canada, because it cannot be changed later.',
            'If your home charger is open to guests but has no guest price, its page warns you, '
                + 'and the app tells you when a guest could not charge, at most once a day.',
            'At a building plug you registered, paid guest charging is not available yet. People '
                + 'you add to your free pass list charge free.',
            'To register a charger at a building, you join the building with its code first.',
            'Your home electricity rate is checked against a limit of $0.40 per kWh, '
                + 'or $0.80 per kWh in a time of use window, to catch typos.',
            'Home session costs are labelled as your own utility estimate, '
                + 'not an EV Initiative charge.',
            'The Store lists the network ready AC Elite Commercial alongside the home chargers.',
            'Our updated terms take effect on 20 October 2026: the Terms of Service for everyone, '
                + 'the Payout Terms if you have a payout account, and the Host Program Terms if '
                + 'your charger is open to guests. The next time you open the app, it shows each '
                + 'one that applies to you to read and accept. You can read them any time '
                + 'at evinitiative.com.',
        ].join('\n'),
    },
    {
        date: '2026-09-26',
        title: 'Hosts keep 85%, and the connection fee goes to EVI',
        // One idea per line; the What's New card renders each line as its own
        // bullet (see the CHANGELOG_ENTRIES header note for the default style).
        body: [
            'Hosts now keep 85% of what guests pay for charging. EVI keeps 15% as its platform fee.',
            'The connection fee is EVI\'s, not the host\'s. EVI sets it for each charger, and a new '
                + 'charger starts at $1.',
            'A driver pays it once per paid session, on top of the price for charging. It never '
                + 'comes out of the host\'s 85%.',
            'When you start in the app, you see the fee before you start. It is also on your receipt.',
            'Free chargers stay free: no connection fee, and no card needed.',
            'Hosts no longer set a plug-in fee. The earn walk and charger settings show EVI\'s fee '
                + 'as a read-only line.',
            'EVI pays for its coupons and points credit out of its own share. A host is never paid '
                + 'less because of one.',
        ].join('\n'),
    },
    {
        date: '2026-09-15',
        title: 'Charging is the home screen while you charge',
        // One idea per line; the What's New card renders each line as its own
        // bullet (see the CHANGELOG_ENTRIES header note for the default style).
        body: [
            'When a session is pending or running, the home tab shows it instead of the map.',
            'The animation runs across the width of the screen. Under it come energy added, cost '
                + 'so far, your rate and elapsed time, then End Session.',
            'Everything is on the page. Nothing sits behind a sheet or a drag handle.',
            'The Find Charger tab still opens the map.',
            'If your theme has a game, Game Time opens it. The numbers and End Session stay in a '
                + 'strip above the game.',
            'On your own home charger the rate reads Free.',
            'After you tap Start, the screen shows Waiting for charger until the charger reports the cable.',
        ].join('\n'),
    },
    {
        date: '2026-09-15',
        title: 'Three shortcuts of your own',
        // One idea per line; the What's New card renders each line as its own
        // bullet (see the CHANGELOG_ENTRIES header note for the default style).
        body: [
            'The plus button on the map and the first row of Menu are now three shortcuts.',
            'By default they are what your role uses most.',
            'A home host gets Start my charger, Earnings and Rewards. A fleet driver gets Start '
                + 'charger, My vehicle and Sessions. Everyone else gets Find charger, Rewards and '
                + 'Friends.',
            'Change them any time from Menu > Your shortcuts.',
            'They are saved to your account, so a new phone or a reinstall keeps them.',
            'EVII stays at the top of the stack.',
        ].join('\n'),
    },
    {
        date: '2026-09-15',
        title: 'Home hosts: earning is one walk from adding your charger',
        // One idea per line; the What's New card renders each line as its own
        // bullet (see the CHANGELOG_ENTRIES header note for the default style).
        body: [
            'After you add a home charger, the app asks: Want to earn from it?',
            'Set the price guests pay. The margin line shows what you keep, 85% after EVI\'s 15% '
                + 'fee, against your electricity estimate. The estimate is optional.',
            'Choose the hours guests can charge.',
            'Set up payouts with Stripe. The app tells you first what Stripe will ask for: your '
                + 'legal name as it appears on your ID, your address, an ID and a selfie, and your '
                + 'bank details. It takes about three minutes.',
            'The app shows Confirming with Stripe while Stripe verifies you. Then you turn '
                + 'earning on.',
            'The connection fee is EVI\'s. EVI sets it for each charger, and the walk shows it as a '
                + 'read-only line.',
            'Guests pay it once per paid session, on top of your price. It never comes out of your '
                + '85%, and free sessions never carry it.',
            'Guest hours run on your charger\'s local time, as they have since August.',
        ].join('\n'),
    },
    {
        date: '2026-09-15',
        title: 'EVII greets you by name',
        // One idea per line; the What's New card renders each line as its own
        // bullet (see the CHANGELOG_ENTRIES header note for the default style).
        body: [
            'EVII now greets you by name.',
            'She knows your three shortcuts and whether you host a charger, and her answers know '
                + 'it too.',
            'She will not tell someone with a wallet to add a card. When you say my charger, she '
                + 'knows which one you mean.',
            'Reach her from Ask EVII on the charging screen, from your shortcuts on the map, or '
                + 'from the help button while you are starting a charge.',
        ].join('\n'),
    },
    {
        date: '2026-09-15',
        title: 'Two tabs in the notifications bell',
        // One idea per line; the What's New card renders each line as its own
        // bullet (see the CHANGELOG_ENTRIES header note for the default style).
        body: [
            'The notifications bell now has two tabs.',
            'Activity is for things you can act on: payments, your charging, guest sessions, '
                + 'charger alerts and friend requests.',
            'News & rewards holds the rest.',
        ].join('\n'),
    },
    {
        date: '2026-09-14',
        title: 'Push notifications',
        // One idea per line; the What's New card renders each line as its own
        // bullet (see the CHANGELOG_ENTRIES header note for the default style).
        body: [
            'Turn on push notifications in Settings > Push notifications.',
            'You get a notification when your charge starts and when it stops.',
            'Categories let you choose what else you hear about: payments and account, your '
                + 'charging, guest sessions on your charger, charger alerts, friends and invites, '
                + 'rewards, and news and offers.',
            'On by default: payments and account, your own charging, guest sessions on your charger, '
                + 'and charger alerts. Friends and invites, rewards, and news and offers stay off until '
                + 'you turn them on.',
        ].join('\n'),
    },
    {
        date: '2026-08-09',
        title: 'Guest hours now run on local time. Check yours.',
        body: [
            'Until today, guest hours on a home charger were enforced in UTC, not in your '
                + 'local time. That was wrong.',
            'If you set 9:00 to 22:00 in Toronto, your charger actually opened at 5:00 and '
                + 'closed at 18:00 your time. Guests were turned away during hours you thought '
                + 'were open.',
            'From today, guest hours run on the charger location’s local time. The hours '
                + 'you type are the hours that apply.',
            'If you worked around the old behaviour by entering shifted hours, those hours are '
                + 'now taken literally and will be wrong by however much you shifted them.',
            'Open My Chargers, look at your guest hours, and set them to the hours you actually '
                + 'mean.',
            'A charger with no timezone set still runs on UTC, and the guest hours section says '
                + 'so on screen. At a home charger, tap Use my timezone to fix it. In a building, '
                + 'ask your building admin to set the timezone.',
        ].join('\n'),
    },
    {
        date: '2026-07-29',
        title: 'Something to play while you charge',
        // One idea per line; the What's New card renders each line as its own
        // bullet (see the CHANGELOG_ENTRIES header note for the default style).
        body: [
            'Some themes now put a game on the charging screen. Look for Game time next to the '
                + 'play button on the charging animation, and tap it.',
            'Power Lap comes with the Neon theme, a 3D kart race across five tracks. '
                + 'Drift to regen, grab charge crates, surge past the pack.',
            'Stay Grounded comes with the Cartographic theme, a garden that keeps growing back. '
                + 'Cutting a cluster off the canopy pays far more than matching three.',
            'Your session stays with you the whole time. The game shows energy, cost and '
                + 'elapsed time as you play, and pauses itself if anything needs your attention.',
            'Game points are for progress and bragging rights inside the game. They are not EVI '
                + 'Points, they are not charging credit, and they cannot be redeemed.',
            "If a charger is free to use, you don't need a card on file to start a charge.",
        ].join('\n'),
    },
    {
        date: '2026-07-12',
        title: 'A warmer welcome, and your language',
        // One idea per line; the What's New card renders each line as its own
        // bullet (see the CHANGELOG_ENTRIES header note for the default style).
        body: [
            'On your first visit I now say hello out loud and introduce myself, in a voice '
                + 'you pick, female or male.',
            'A clearer welcome: tell me how you charge, Public, Apartments & Condos, or Home '
                + 'Charging, and I tailor your tour to match.',
            'Announcements now wait in your notifications bell, not just on the map. Tap one '
                + 'to open it again anytime.',
            'Our terms and payout agreements are now versioned, with clear accept screens so '
                + 'you always know what you agreed to.',
            'A new marketing updates toggle in Settings, with bonus points the first time you '
                + 'turn it on. Off by default, unsubscribe anytime.',
            'Language support begins: pick from 8 languages in Settings, with more of the app '
                + 'translated over time.',
            'Charger alerts now greet you the moment something needs attention, and walk you '
                + 'through reconnecting step by step.',
        ].join('\n'),
    },
    {
        date: '2026-07-09',
        title: 'The July drop',
        // One idea per line; the What's New card renders each line as its own
        // bullet (see the CHANGELOG_ENTRIES header note for the default style).
        body: [
            'The guided tour: I walk you through the app the first time you sign in. Want it '
                + 'again? Ask me, or tap Take the tour in Help.',
            'Charging animations: Initiative, Electric Light, Electric Dark, and Luce each '
                + 'bring their own live animation to the charging screen, and it responds to your '
                + 'charge as the energy flows.',
            'Profile is now Menu. Same tab, clearer name.',
            'New theme milestones: Sunset opens at 250 kWh, Bloom at 550, Aurora at 850, '
                + 'and Cyber at 1,150.',
            'Receipts now show the points you earned on every session.',
            'Announcements: news lands as a small card when you open the app, and waits in '
                + 'your notification bell.',
            'The public shop is live at evinitiative.shop, with DC fast chargers and an '
                + 'incentives finder for rebates where you live.',
            'I am on the shop too. Ask me about any charger before you buy.',
            'The Sales Partner program: earn commission introducing EV Initiative to sites '
                + 'that need charging. Details on the shop.',
        ].join('\n'),
    },
    {
        date: '2026-07-03',
        title: 'My Home, rebuilt',
        body: [
            'A cleaner dashboard with a live charger card, one-tap start, and your home '
                + 'charging history with charts.',
            'Tap any session for the full detail.',
        ].join('\n'),
    },
    {
        date: '2026-07-03',
        title: 'Home Settings',
        body: 'Name your home and your charger, set your address, add your utility rate to see '
            + 'what charging would have cost, and cap your charging speed.',
    },
    {
        date: '2026-07-03',
        title: 'Invite anyone to charge free at home',
        body: [
            'Add family and friends to your free pass list by email.',
            'No app yet? They get a join link and land on your list when they sign up.',
        ].join('\n'),
    },
    {
        date: '2026-07-03',
        title: 'The Store',
        body: [
            'Buy home and commercial chargers, RFID cards, and accessories in the app.',
            'Secure card checkout, prices in USD.',
        ].join('\n'),
    },
    {
        date: '2026-07-03',
        title: 'Better history',
        body: 'Charts now cover every time range, and every session opens to a full detail view '
            + 'with your points.',
    },
    {
        date: '2026-07-03',
        title: 'Sharper details everywhere',
        body: 'Product pages with films, session receipts that show your points, and a cleaner '
            + 'look on iPhone.',
    },
    {
        date: '2026-06-18',
        title: 'EVII can take you there',
        body: [
            'Ask EVII for a screen and she offers a button that takes you straight to it.',
            'How do I see my history? Tap, and you are there.',
            'She only ever offers. Your tap is what moves you.',
        ].join('\n'),
    },
    {
        date: '2026-06-17',
        title: 'A manual, right in the app',
        body: [
            'There is a Help section now, in your Menu.',
            "Browse the manual to learn any part of the app, and open What's New to see what "
                + 'just landed.',
            'EVII reads the same manual, so her answers and these pages always agree.',
        ].join('\n'),
    },
    {
        date: '2026-06-13',
        title: 'EVII can pull your numbers',
        body: [
            'EVII can now look up your own data on request.',
            'Get your points and rank, your all-time charging stats, and your recent sessions.',
            'Ask her for a receipt over a date range and she prepares it for you to download.',
        ].join('\n'),
    },
    {
        date: '2026-06-10',
        title: 'A cleaner app',
        body: [
            'We sharpened the everyday details.',
            'The bottom navigation reads clearer, the theme picker opens cleanly on every '
                + 'screen, and the chat stays put while you scroll back through a reply.',
        ].join('\n'),
    },
    {
        date: '2026-06-06',
        title: 'Meet EVII, your assistant',
        body: [
            'EVII arrived to help inside the app.',
            'Ask her how to start a charge, find a station, or work any screen.',
            'Reach her from the quick actions on the map.',
            'She sticks to charging and the EV Initiative app.',
        ].join('\n'),
    },
];
export const MANUAL_PAGES = [
    {
        // WRITTEN FOR SOMEONE WHO DOES NOT HAVE THE APP YET. Every other page
        // addresses a driver holding the phone, which is why the shop assistant
        // could not use them: `starting-a-charge` is a tap-by-tap walkthrough,
        // useless to a prospect. This page explains what charging IS. No screen
        // names, no tap instructions, no second person about the app — so it
        // reads correctly on the public storefront AND in the driver Help.
        slug: 'how-charging-works',
        title: 'How Charging Works',
        summary: 'What happens when you pull up to a charger, how you pay, and how you know '
            + 'what it cost.',
        audience: 'driver',
        related: ['starting-a-charge', 'payment-and-wallet', 'rfid-cards', 'software-pricing'],
        body: [
            'A charging session starts when you plug in and identify yourself, and it ends when '
                + 'you stop it or unplug. Everything between those two moments is the session: the '
                + 'energy that moves into the car, how long it takes, and what it costs.',
            'At a public charger you identify yourself one of two ways. You can start the charge '
                + 'from your phone, or you can hold an RFID card or keyfob against the reader on the '
                + 'charger. Both do the same job. They tell the charger who is charging, so the '
                + 'session bills to the right account.',
            'An RFID card is the option that does not need a phone. It is a card or keyfob you '
                + 'hold against the charger, and it earns its place when a phone is dead, when '
                + 'signal is poor in an underground garage, or when you would rather not get a phone '
                + 'out in the rain. One credential works at every EV Initiative charger your account '
                + 'can use.',
            'Payment sits on the account, not on the charger. A card or a prepaid balance is '
                + 'attached once, and every session draws from it. There is no card terminal on the '
                + 'charger and nothing to settle at the site when you leave.',
            'What a session costs depends on who owns the charger, because the owner sets the '
                + 'rate. The rate is shown before the charge starts, so the price is known going in '
                + 'rather than discovered at the end. Most chargers bill for the energy delivered, '
                + 'measured in kWh, and some also bill for the time the connector is occupied.',
            'When the session ends it produces an itemised receipt: the energy delivered, how '
                + 'long it took, the rate applied, and the total. That receipt is kept, so a month '
                + 'of charging can be looked back over or handed to an employer.',
            'Charging at home works differently. Electricity for your own charger reaches you on '
                + 'your utility bill as it always did, and EV Initiative does not charge you to use '
                + 'a charger you own. If you open it to other drivers, they pay the rate you set and '
                + 'you keep the large majority of it.',
            'When a charge will not start, it is almost always one of three things: the charger '
                + 'is offline, the connector is not seated firmly enough for the car and charger to '
                + 'agree, or the account has no working payment method. Support can say which.',
        ].join('\n\n'),
    },
    {
        slug: 'getting-started',
        title: 'Getting Started',
        summary: 'Signing in, finding your way around, and the one thing to do before your first charge.',
        audience: 'driver',
        related: ['payment-and-wallet', 'starting-a-charge', 'notifications-and-account'],
        body: [
            'Welcome to EV Initiative. Here is the lay of the land so your first charge goes '
                + 'smoothly.',
            'Signing in is quick. You can sign in with a magic link sent to your email, or with '
                + 'your Google account. You land in the same place each time, ready to find a charger '
                + 'or start one.',
            'The app has three areas, and the bar at the bottom moves you between them. Find '
                + 'Charger is the map of stations near you. Start Charger is the fast way to begin a '
                + 'session at the charger in front of you. Menu is where your account, your money, '
                + 'your history, and your rewards live.',
            'Before your first charge, do one thing: add a payment method. The app needs one on '
                + 'file before it will start a session. It takes a minute and you only do it once. See '
                + 'Payment and Wallet, then head to Starting a Charge when you are ready to plug in.',
        ].join('\n\n'),
    },
    {
        slug: 'payment-and-wallet',
        title: 'Payment and Wallet',
        summary: 'Add a payment method, understand your wallet credit, and find your coupons, in one place.',
        audience: 'driver',
        related: ['starting-a-charge', 'coupons', 'getting-started'],
        body: [
            'Everything to do with paying for a charge lives under Money in your Menu. Set it '
                + 'up once, and charging works on its own from then on.',
            'Add a payment method before your first charge. Open the Menu, go to Money, and add a '
                + 'card. If you have Apple Pay or Google Pay set up on your phone, you can use that '
                + 'instead of entering a card by hand. Once a method is on file, the app charges it '
                + 'automatically each time you finish a session.',
            'If you keep more than one method, one of them is your default. The default is what '
                + 'the app uses unless you pick another on the charge screen. Change which one is the '
                + 'default in Money whenever you want.',
            'Wallet credit is money held on your account, separate from your card. When you start '
                + 'a charge you choose which to use: pay from your wallet balance, or charge your '
                + 'card. A wallet session draws from your balance, so keep enough credit on hand to '
                + 'cover the charge. Your balance shows in Money.',
            'Your coupons live here too. Any promotional code you hold sits under Money, ready to '
                + 'use against a charge. See Coupons for how they work.',
        ].join('\n\n'),
    },
    {
        slug: 'starting-a-charge',
        title: 'Starting a Charge',
        summary: 'The two ways to start a charge, from payment setup through to the live session.',
        audience: 'driver',
        related: ['payment-and-wallet', 'sessions-and-receipts', 'getting-help'],
        body: [
            'Before anything else, set up a payment method. The app will not start a session '
                + 'without one on file, so if you have not added a card or wallet credit yet, do that '
                + 'first. It takes a minute and you only do it once. (See Payment and Wallet.)',
            'With that done, there are two ways to start a charge, and both land you on the same '
                + 'screen. Use whichever is easier in the moment.',
            'The QR code is the fast way. Tap Start Charger in the bottom bar and point your camera '
                + 'at the code on the charger. The app reads it and puts you straight onto that exact '
                + 'unit, with no searching and no guessing which stall you are at. Reach for this when '
                + 'you are at the charger and ready to plug in.',
            'The map is the other way. Tap Find Charger, open the station you want, and look at its '
                + 'stalls. Open stalls are marked, so pick one and you reach the same charge screen. Use '
                + 'this when you are planning ahead, checking a station before you drive over, or the '
                + 'code will not scan.',
            'On the charge screen you confirm and go. Pick the payment method for this session. If '
                + 'you have only one, it is already chosen. If you have several, the app uses your '
                + 'default unless you pick another. Plug the connector into your car if you have not '
                + 'already, then start the session.',
            'The app waits for real power before it counts the session as live. That short pause is '
                + 'it confirming the car and the charger have connected and current is flowing. The '
                + 'moment that happens, the screen switches to the live view.',
            'The live view is where the session runs. A power reading shows how fast you are '
                + 'charging, and a ring fills as your battery does. Below them, three numbers climb: the '
                + 'energy added, the time on the session, and the cost. You do not need to stay on the '
                + 'screen, or even keep the app open. The session runs on its own, so lock your phone, '
                + 'walk away, and check back whenever you like.',
            'When you are done, tap End Session at the bottom of the screen. That stops the charger '
                + 'and takes you straight to a summary of the session. Unplug, and the receipt is already '
                + 'saved to your history. (See Sessions and Receipts to find it, download it, or export '
                + 'a batch.)',
            'If a charge will not start, check that the connector is fully seated and your payment '
                + 'method is valid, then try again. If it still will not go, Getting Help walks you '
                + 'through the next steps.',
        ].join('\n\n'),
    },
    {
        slug: 'sessions-and-receipts',
        title: 'Sessions and Receipts',
        summary: 'Where to find your charging history, a single receipt, or a full export for expenses.',
        audience: 'driver',
        related: ['starting-a-charge', 'payment-and-wallet', 'idle-fees', 'getting-help'],
        body: [
            'Every charge you have ever run is saved. You will find them under Charging History in '
                + 'your Menu.',
            'The list shows your sessions newest first. Each row carries the date, the location, '
                + 'the energy delivered, and the cost, so you can scan your recent charging at a '
                + 'glance. Tap any session to open its full detail, including its Session ID and '
                + 'the points you earned.',
            'Your history also charts itself. Pick any time range, including 90 days, 1 year, '
                + 'all time, or a custom range, to see your charging over time and your top '
                + 'locations.',
            'When you need a receipt, you have two ways to get one. Download a single session for '
                + 'one charge, or export a whole date range as a PDF or CSV when you are putting '
                + 'expenses together. The export covers every session in the range you pick.',
            'Your history fills in on its own. The moment a session ends, its receipt is already '
                + 'there, so there is nothing to save by hand. If the cable stays connected and an idle fee applies, '
                + 'the receipt updates with charger-confirmed idle. Idle after Stop is charged separately on the same session.',
        ].join('\n\n'),
    },
    {
        slug: 'idle-fees',
        title: 'Idle Fees and Unplugging',
        summary: 'The free grace period, the idle maximum and why stopping charging does not end idle fees.',
        audience: 'driver',
        related: ['starting-a-charge', 'sessions-and-receipts', 'payment-and-wallet'],
        body: idleManualBody(),
    },
    {
        slug: 'coupons',
        title: 'Coupons',
        summary: 'What promotional codes are, how to add one, and where it applies.',
        audience: 'driver',
        related: ['payment-and-wallet', 'sessions-and-receipts'],
        body: [
            'From time to time, EV Initiative releases promotional codes, whether for a launch, a '
                + 'partner deal, or a one-off offer. When one is available to you, it shows up in your '
                + 'Coupons list, ready to claim.',
            'You find your coupons under Money in your Menu. Tap Claim to add one to your '
                + 'account. Once claimed, it comes off the cost of an eligible charge or wallet top-up '
                + 'automatically, so there is nothing extra to do at the charger. Your coupons stay '
                + 'listed under Money, so you can always see what you are holding.',
            'A coupon changes nothing about how you charge. You start a session the usual way, and '
                + 'the discount applies on its own.',
        ].join('\n\n'),
    },
    {
        slug: 'favorites',
        title: 'Favorites',
        summary: 'Save the stations you use so they are a tap away.',
        audience: 'driver',
        related: ['starting-a-charge', 'sessions-and-receipts'],
        body: [
            'If you charge at the same few places, save them as favorites so you are not '
                + 'searching for them every time.',
            'You favorite a station from its page, and your saved stations are then quick to reach '
                + 'when you go to start a charge.',
            'Favorites are purely a shortcut. They do not change how a charge works or what it '
                + 'costs. They only make the places you keep coming back to faster to find.',
        ].join('\n\n'),
    },
    {
        slug: 'points-and-rewards',
        title: 'Points and Rewards',
        summary: 'How you earn points, the ranks they unlock, badges, streaks, gifting, and '
            + 'turning points into credit.',
        audience: 'driver',
        related: ['themes', 'payment-and-wallet', 'starting-a-charge'],
        body: [
            'You earn points every time you charge, and those points do three things: move you up '
                + 'through ranks, unlock badges, and convert into credit you can spend. Here is how '
                + 'the whole system fits together.',
            'The base is simple. Every session earns you one point for each kWh you deliver, plus '
                + 'five points for finishing the charge. Once you reach the Voltage rank and beyond, '
                + 'each point you earn is worth a little more on top of that.',
            'Several things earn you extra. Charge at least once in a week and you build a weekly '
                + 'streak, with a points bonus at four, eight, and twelve weeks. Miss a week and it '
                + 'resets. Charge in the same week as a confirmed friend and you both earn for it, '
                + 'with larger bonuses the longer you keep it going together. Bring someone new in, '
                + 'and you earn again as they finish their first, fifth, and tenth sessions. Review '
                + 'a place you have charged, and you earn a few points for that as well. There '
                + 'are also weekly challenges, set for you automatically, that pay out when you '
                + 'complete them.',
            'Points carry you up through five ranks. Everyone starts at Sprout. Next is Charger at '
                + '250 points, then Voltage at 1,000, Power Pioneer at 5,000, and Grid Master at the '
                + 'top at 25,000. Rank goes on lifetime points, so spending points never costs you a '
                + 'rank and once you reach one you keep it. The Rewards page shows where you stand '
                + 'and how far you are from the next rank, and each rank you reach unlocks new avatar '
                + 'skins.',
            'Badges mark what you do along the way. There are dozens, for milestones like your '
                + 'first charge and your hundredth, the energy you have delivered, late-night or '
                + 'early-morning sessions, bringing friends in, and more. Each badge you earn pays '
                + 'out points too, more for the rarer ones. Earned badges fill a grid on the Rewards '
                + 'page, and the ones you have not reached yet stay hidden until you do.',
            'You can also send points to friends. From a friend\'s page, gift them points in a few '
                + 'taps, up to 500 a month per friend, and that ceiling doubles to 1,000 if the two '
                + 'of you have a running friendship streak. A gift is a handoff: your friend can '
                + 'spend what you send, though it does not count toward their rank.',
            'When you want to use your points, they are worth real money. A hundred points is a '
                + 'dollar, and you can redeem from a hundred up. Send them to your wallet to spend on '
                + 'anything, or put them toward your next charge. How much you can cash out each '
                + 'month rises with your rank, and spending points never lowers your lifetime total '
                + 'or your rank, so it does not set you back.',
            'Two last things worth knowing. A session has to deliver at least a little energy over '
                + 'a few minutes to count toward points. And your points keep as long as you keep '
                + 'earning: go twelve months without earning any and the whole balance clears at '
                + 'once, so it is the gap that costs you rather than the age of any single point. '
                + 'Anything that earns you points starts the twelve months over, including a gift '
                + 'from a friend.',
        ].join('\n\n'),
    },
    {
        slug: 'themes',
        title: 'Themes',
        summary: 'How to change the look of the app, and how new themes unlock as you charge.',
        audience: 'driver',
        related: ['charging-animations', 'points-and-rewards', 'getting-started'],
        body: [
            'The app does not have to look the same for everyone. Tap the palette button at the '
                + 'top of any screen to change its theme, and to flip between light and dark.',
            'A set of themes is yours from the start. Tap View all your themes to see the full '
                + 'collection and switch between them freely.',
            'Some themes you unlock by charging. As your total energy climbs, new looks open at '
                + 'set milestones: Sunset at 250 kWh, Bloom at 550, Aurora at 850, and Cyber at 1,150. '
                + 'They arrive on their own as you pass each mark, so the app changes with you the '
                + 'more you drive.',
            'Several themes also bring their own live charging animation, a moving artwork on '
                + 'the charging screen that responds to your session. See Charging Animations for '
                + 'which themes have one and how they behave.',
            'More themes land over time, so it is worth checking back. What you see today is not '
                + 'the whole set forever.',
        ].join('\n\n'),
    },
    {
        slug: 'charging-animations',
        title: 'Charging Animations',
        summary: 'Live, theme-specific artwork on the charging screen that responds to your session.',
        audience: 'driver',
        related: ['themes', 'games', 'starting-a-charge'],
        body: [
            'While you charge, the charging screen plays a live animation, and each of these '
                + 'themes has its own: Initiative flows like marbled ink, Electric Light grows a '
                + 'glowing network, Electric Dark swirls like electric dye, and Luce ripples like '
                + 'woven light.',
            'They are not loops. Each one responds to your session, gathering energy and '
                + 'coming further alive as the kilowatt hours add up, so a charge that is nearly '
                + 'done looks different from one that just started.',
            'They are polite about your battery too. The animation runs only while the '
                + 'charging screen is open, pauses when you leave or switch apps, and keeps its '
                + 'frame rate modest.',
            'If your phone is set to reduce motion, the app respects that and shows a calm '
                + 'still version instead. If your phone cannot run the animation, you get a '
                + 'clean gradient and everything else works exactly the same.',
        ].join('\n\n'),
    },
    {
        slug: 'games',
        title: 'Games while you charge',
        summary: 'Some themes put a game on the charging screen, and your session stays '
            + 'visible the whole time.',
        audience: 'driver',
        related: ['charging-animations', 'themes', 'points-and-rewards'],
        body: [
            'Some app themes come with a game built into the charging screen. It is there to '
                + 'make a long charge shorter, and it never gets in the way of the session itself.',
            'Open a charging session and look at the charging animation. If your theme has a '
                + 'game, you will see Game time next to a play button. Tap it and the game opens '
                + 'full screen.',
            'Nine games are available. INITIATIVE (Default) has Blackout Run. Neon has Power '
                + 'Lap. Cartographic has Stay Grounded. Drip has Overflow. Luce has Full Charge. '
                + 'Aurora has Current. Cyber has Surge Pack. The Midnight skin has Steady Climbing and the Sunset skin '
                + 'has Volley. An applied skin\'s game takes precedence over its base theme. '
                + 'Switch themes from the palette in the top bar.',
            'Your session keeps running while you play. The game shows it across the top: '
                + 'energy delivered, cost so far, and elapsed time, all updating as you go.',
            'If anything needs your attention, the game pauses itself and tells you why. That '
                + 'covers the session ending, the charger disconnecting or faulting, and a payment '
                + 'problem. You will not miss something important because you were playing.',
            'Tap Exit any time and you come straight back to your session. Leaving the app '
                + 'pauses the game on its own, so it will not run in your pocket.',
            'Power Lap, on the Neon theme. A 3D kart race with seven drivers in electric SUVs '
                + 'across five tracks. Drag anywhere to steer. Hold a hard turn to drift, which '
                + 'runs regen and fills your battery, then tap the full battery to surge. Drive '
                + 'through EVI charge crates for items and tap the screen to use one. Tap in the '
                + 'air off a ramp for a trick boost. Race single tracks or the five-track EVI '
                + 'Cup, in three classes.',
            'Controls: drag anywhere to steer, hold a hard turn to drift, tap the full battery '
                + 'to surge, tap the screen to use an item.',
            'Blackout Run, on the Default theme. A rooftop chase across six stages. The Drain '
                + 'is pulling the city dark, and you carry the charge. Swipe up to jump, vault '
                + 'and grab. Swipe down to slide or roll. Hold to glide. Each stage runs 4 to 10 '
                + 'districts with bar swings, rope swings, wall runs, rail grinds and collapsing '
                + 'bridges, and ends with a leap to an EVI beacon that unlocks the next stage.',
            'Controls: swipe up to jump, vault and grab, swipe down to slide or roll, hold to glide.',
            'Surge Pack, on the Cyber theme. Hold anywhere to fly and release to drop. Dodge '
                + 'zappers, lasers and rockets, collect coins and vehicle tokens, and try the '
                + 'rides in the garage. Finish missions to earn stars and advance through 15 '
                + 'ranks per cycle. Follow each vehicle\'s on-screen controls. Coins and prizes '
                + 'are for this game only, never charging credit.',
            'Controls: hold anywhere to fly, release to drop, and follow the controls shown '
                + 'for each vehicle.',
            'Stay Grounded is a garden that keeps growing back. Drag to aim and release to '
                + 'sow. Land three of a kind and they bloom away, and cutting a cluster off the '
                + 'canopy drops everything unsupported.',
            'Overflow is a wall of cells. Break them and they overflow, so catch the drips '
                + 'with your collector. Where you catch the ball is where it goes.',
            'Full Charge is a bay you pack with cells. Drag to slide, tap to turn, swipe down '
                + 'to slam. A full row discharges into the reserve, and a full reserve dumps the '
                + 'bottom three rows.',
            'Current runs a charge down a conduit. Tap to lift, gravity does the rest, and '
                + 'the loose energy in the gaps is worth the tight line.',
            'Steady Climbing is one button and a hundred floors. A slab slides across, you '
                + 'drop it, and whatever hangs over the edge shears off. The higher you get the '
                + 'further you can see.',
            'Volley is a duel with the grid. Every return loads your paddle and a full load '
                + 'fires a surge. Past twelve returns the packet turns unstable and the point '
                + 'counts double.',
            'Playing earns points, which raise your rank and unlock cars and tracks. These are '
                + 'separate from EVI Points. Game points are for progress and bragging rights '
                + 'inside the game. They are not charging credit, and they cannot be redeemed.',
            'If a game will not open, check your theme first, since only some themes have games. '
                + 'If the play button is there and nothing happens, close and reopen the session. '
                + 'The games need no signal once loaded, so a weak connection in a parking garage is '
                + 'not the problem.',
        ].join('\n\n'),
    },
    {
        slug: 'rfid-cards',
        title: 'RFID Cards',
        summary: 'Use a tap card to start a charge without your phone.',
        audience: 'driver',
        related: ['starting-a-charge', 'payment-and-wallet'],
        body: [
            'An RFID card lets you start a charge with a tap, no phone needed. The card is issued '
                + 'and linked to your account by your building or charger operator, so it is ready to '
                + 'use at their chargers.',
            'Setting a card up may carry a small one-time fee, depending on the site. Once it is '
                + 'linked, tap it on a charger to begin a session, billed to your account just like a '
                + 'charge you start in the app.',
            'A tapped session works exactly like one from the app. It runs, bills your account, '
                + 'and lands in your Charging History with everything else.',
        ].join('\n\n'),
    },
    {
        slug: 'joining-a-building',
        title: 'Joining a Building',
        summary: 'If your building has EV Initiative chargers, join it with a code to use them.',
        audience: 'driver',
        related: ['starting-a-charge', 'getting-started'],
        body: [
            'Some buildings run EV Initiative chargers for their residents. If yours does, you '
                + 'join the building with a code, and its chargers open up to you.',
            'You enter your building\'s join code under Account in your Menu. Once you are in, '
                + 'the building\'s chargers are available for you to charge at, alongside the public '
                + 'network.',
            'Joining a building changes nothing else about the app. You still charge the usual '
                + 'way, and your sessions and receipts work exactly as they do anywhere. It simply '
                + 'adds your building\'s chargers to what you can use.',
            'Chargers shared with you in other ways have their own page too. 3rd-Party '
                + 'Chargers lists every charger someone has shared with you, including home '
                + 'chargers whose free pass list you are on.',
        ].join('\n\n'),
    },
    {
        slug: 'my-home',
        title: 'My Home',
        summary: 'Your home charging dashboard: live status, one-tap start, history, and settings.',
        audience: 'host',
        related: ['starting-a-charge', 'reconnecting-your-charger', 'sessions-and-receipts', 'store', 'software-pricing'],
        body: [
            'My Home is your home charging dashboard. See your charger\'s status live, start '
                + 'charging with one tap, and view your home session history with charts. Tap any '
                + 'session for its full details.',
            // Register first (Warren, 2026-10-02): the labels match the app's
            // buttons and the connection card (Server URL, Charger ID, Password,
            // Open setup for 30 minutes). The blank-password setup window opens at
            // registration and lasts 30 minutes (homeFirstConnection.ts).
            'Set up in this order: register first, then connect. Tap Set up My Home and add '
                + 'your address. Then tap Register my home charger and enter the charger ID. Your '
                + 'charger does not need to be online yet. My Home then shows a server URL, a '
                + 'charger ID and a password. Enter them in the Autel app, and your charger '
                + 'connects. If My Home says to leave the password blank, leave it blank. That '
                + 'setup stays open for 30 minutes after you register. If it has closed by the '
                + 'time you reach the charger, tap Open setup for 30 minutes, or enter the '
                + 'password My Home shows.',
            // "Sell it under Home" (Warren, 2026-10-02): the network-ready model is the
            // AC Elite Commercial, listed with the home chargers.
            'Your charger needs to be a network-ready model to connect to EV Initiative. Buy '
                + 'the AC Elite Commercial, listed with the home chargers in the Store and at '
                + 'evinitiative.shop. The AC Elite Home does not connect to the EV Initiative '
                + 'network.',
            'Day to day, your home charger is managed here, in the EV Initiative app. The '
                + 'Autel app is only for first-time setup: pairing over Bluetooth, joining your '
                + 'Wi-Fi and entering the details My Home shows. After that, everything lives in '
                + 'My Home: starting a charge, your history, your settings, and your free pass '
                + 'list.',
            'Home Settings is the gear from My Home. Name your home and your charger, and set '
                + 'your address. Enter your utility electricity rate to see estimated costs; home '
                + 'charging stays free, the rate is only for your own tracking. You can also cap '
                + 'your charging speed in amps.',
            'Your free pass list lives in Home Settings too. Invite family and friends by '
                + 'email to charge free at your home. If they do not have the app yet, they get '
                + 'an email with a join link and land on your free pass list the moment they '
                + 'sign up. You can revoke a pending invite any time.',
            'Home charging is free for you as the owner and for everyone on your free pass '
                + 'list.',
        ].join('\n\n'),
    },
    {
        slug: 'reconnecting-your-charger',
        title: 'Reconnecting Your Charger',
        summary: 'What to do when your home charger shows offline, from a one-tap nudge to a '
            + 'guided walkthrough back online.',
        audience: 'host',
        related: ['my-home', 'getting-help', 'ask-evie'],
        body: [
            'Your home charger stays online by keeping a live connection to us over your home '
                + 'Wi-Fi. Now and then that connection drops: the power blinks, your router '
                + 'restarts, or the charger just loses its link. When it does, My Home shows the '
                + 'charger as offline and the Start button steps aside for a recovery path. You '
                + 'are never left with a dead button.',
            'Start with a nudge. When the charger reads offline, tap Nudge charger. If the '
                + 'charger is still reachable behind the scenes, I wake it and watch for it to '
                + 'report back in. You will see "watching for it to come back online," and the '
                + 'moment it does, the page flips back to ready and Start returns on its own. This '
                + 'clears the most common case, a charger that quietly dozed off, in one tap.',
            'If a nudge cannot reach it, I walk you through getting it back, and it is usually '
                + 'quick. First, check the power: make sure the charger has power and its breaker '
                + 'has not tripped. If the breaker is off, switch it back on. Second, power-cycle '
                + 'the charger: turn it off at the breaker, wait ten seconds, then turn it back '
                + 'on. Give it a minute to boot and reconnect. Third, check your Wi-Fi: if your '
                + 'home internet or router went down, the charger cannot reach us. Restart your '
                + 'router if you need to and wait for your Wi-Fi to come back.',
            'While you work through those steps, I keep watching. As soon as your charger '
                + 'announces itself again, I flip it back to online for you and let you know, so '
                + 'you do not have to keep refreshing to check.',
            'If it still will not come back after a power-cycle and a healthy Wi-Fi connection, '
                + 'the charger may need setup again in the Autel app, the one you used when it was '
                + 'first installed, or it may need a hand. Ask me, or reach the team through '
                + 'Support or at support@evinitiative.com.',
        ].join('\n\n'),
    },
    {
        slug: 'store',
        title: 'The Store',
        summary: 'Buy chargers, RFID cards, and accessories right in the app.',
        audience: 'driver',
        related: ['incentives-finder', 'rfid-cards', 'getting-help'],
        body: [
            'The Store sells home and commercial AC chargers, RFID cards, and accessories, '
                + 'right in the app. Prices are in USD.',
            'For a home charger on the EV Initiative network, choose the AC Elite Commercial '
                + 'in the Home section. It connects over your home Wi-Fi, and you register it in '
                + 'My Home. The AC Elite Home does not connect to the network, so you cannot '
                + 'start it from the app, share it, or earn from it.',
            'You pay by card and enter your shipping address at checkout.',
            'The full store lives at evinitiative.shop, where you will also find DC fast '
                + 'chargers, the incentives finder, and EVII ready to answer questions about '
                + 'any charger before you buy.',
        ].join('\n\n'),
    },
    {
        slug: 'incentives-finder',
        title: 'The Incentives Finder',
        summary: 'Find EV charging rebates and incentives for your area, with honest freshness dates.',
        audience: 'driver',
        related: ['store', 'getting-help'],
        body: [
            'Charging equipment often qualifies for rebates, tax credits, or utility '
                + 'programs. The incentives finder collects them in one place: open '
                + 'evinitiative.shop and choose Incentives, or follow the link from any '
                + 'charger page on the shop.',
            'Pick your country and your state or province, and it lists the programs that '
                + 'may apply: federal programs in the United States and Canada, plus state, '
                + 'provincial, and utility programs where we have them.',
            'A word on honesty: incentive programs change, run out of funding, and close. '
                + 'Every program we curate shows the date we last verified it, so you can see '
                + 'how fresh the information is. Before you count on a number, confirm the '
                + 'details on the official program page it links to.',
            'If a program you know about is missing, tell us through Support and we will '
                + 'add it to the list.',
        ].join('\n\n'),
    },
    {
        slug: 'ask-evie',
        title: 'Ask EVII',
        summary: 'What EVII can do for you, and how to reach her.',
        audience: 'driver',
        related: ['getting-help', 'points-and-rewards', 'sessions-and-receipts'],
        body: [
            'EVII is the assistant built into the app. She is there to answer questions and to '
                + 'save you digging for things.',
            'Reach her from Ask EVII on the charging screen, from your shortcuts on the map (the '
                + 'plus button, where she sits at the top), or from the help button while you are '
                + 'starting a charge. Ask her how something works, or ask about your own account. She '
                + 'knows your points, your charging stats, and your recent sessions, and she will '
                + 'pull up a receipt when you ask.',
            'EVII can also take you where you need to go. Ask how to do something and she can open '
                + 'the right screen for you, rather than leaving you to find it. When a question '
                + 'falls outside the app, she points you to support.',
            'Treat her as the fast path. Anything you would otherwise hunt through menus for, you '
                + 'can usually just ask.',
        ].join('\n\n'),
    },
    {
        slug: 'guided-tour',
        title: 'The guided tour',
        summary: 'A quick, guided walkthrough of the app with EVII.',
        audience: 'driver',
        related: ['ask-evie', 'getting-started', 'getting-help'],
        body: [
            'The guided tour is a short, hands-on walkthrough. EVII fades in over the map, asks '
                + 'what kind of driver you are, and walks you through the essentials from there. You '
                + 'can skip a step or exit at any time.',
            'First I introduce myself and let you pick my voice, then you choose the version that fits '
                + 'you. Public is for charging around town. Apartments & Condos is for shared charging '
                + 'where you live. Home Charging is for setting up a charger at your own place. Fleet is '
                + 'detected automatically when your company covers your charging. Each one starts with the '
                + 'basics everyone needs, then adds the parts that matter for how you drive.',
            'It runs once on its own the first time you sign in. After that it is always here: go to '
                + 'Help and tap Take the tour at the top, or just ask me to show you around and I will '
                + 'start it.',
        ].join('\n\n'),
    },
    {
        slug: 'announcements',
        title: 'Announcements',
        summary: 'The news card on the map, how it lives in your bell, and the two ways to dismiss it.',
        audience: 'driver',
        related: ['notifications-and-account', 'getting-started'],
        body: [
            'Now and then, news lands as a small card over the map when you open the app: a '
                + 'promotion, a new feature, or something about your site. It floats above the '
                + 'map without blocking it, and at most one pops up per visit.',
            'Closing the card with its X just sets it aside for now. It is not gone: it moves '
                + 'to your notification bell, and it may greet you again as a card next time you '
                + 'open the app. Think of the card X as "not right now", not "never".',
            'Every current announcement also waits in your notification bell, at the top of the '
                + 'list with a megaphone label. Tap one there to open its card again whenever you '
                + 'want a second look.',
            'To clear an announcement for good, dismiss it from the bell with its X: that '
                + 'retires it everywhere, on this phone and any other, and it never returns. '
                + 'Tapping the card button (if it has one) does the same.',
            'Announcements are still not push notifications. They only appear inside the app, '
                + 'they never buzz your phone, and your notification preferences are untouched.',
        ].join('\n\n'),
    },
    {
        slug: 'notifications-and-account',
        title: 'Notifications and Account',
        summary: 'Your notification preferences, the weekly summary, and managing your account.',
        audience: 'driver',
        related: ['getting-started', 'payment-and-wallet', 'getting-help'],
        body: [
            'Your account settings live under Account in your Menu. This is where you keep your '
                + 'details current and choose what the app sends you.',
            'Update your settings here, add or change your phone number, and sign out when you '
                + 'need to. My EV, your vehicle profile, now lives under Settings too. Closing '
                + 'your account is not something you can do in the app yet; to request it, email '
                + 'support@evinitiative.com.',
            'EV Initiative also emails you a weekly summary of your charging: your sessions, '
                + 'energy, and spend for the week, with where you stand on rewards. You control what '
                + 'lands in your inbox from your notification preferences, which you can reach from '
                + 'the email itself or from your account settings.',
        ].join('\n\n'),
    },
    {
        slug: 'getting-help',
        title: 'Getting Help',
        summary: 'What to try when a charge will not behave, and how to reach a person.',
        audience: 'driver',
        related: ['starting-a-charge', 'payment-and-wallet', 'reconnecting-your-charger', 'ask-evie'],
        body: [
            'Most charging hiccups come down to a few things, and you can usually clear them '
                + 'yourself in a moment.',
            'If a charge will not start, check that the connector is seated all the way into your '
                + 'car and that your payment method is valid under Money. Reseat the plug, then try '
                + 'again. If the charger looks asleep or unresponsive, unplug, wait a moment, and '
                + 'plug back in to wake it.',
            'If a session ended sooner than you expected, open it in Charging History to see what '
                + 'was delivered and what you were charged. The detail view shows the full picture.',
            'When you are stuck and want a hand, ask EVII. She answers most questions about the '
                + 'app and your account on the spot, and when something needs a real person, she '
                + 'points you to the support form, where you can send our team a message. You can '
                + 'also email support@evinitiative.com directly.',
        ].join('\n\n'),
    },
    {
        slug: 'fleets',
        title: 'Fleets',
        summary: 'What fleet membership changes, joining a fleet, fleet versus public mode, and '
            + 'how points work.',
        audience: 'fleet',
        related: ['starting-a-charge', 'payment-and-wallet', 'points-and-rewards', 'software-pricing'],
        body: [
            'If you drive for an organization that runs a fleet on EV Initiative, fleet membership '
                + 'changes who pays for your charging and how you switch between fleet and personal '
                + 'driving.',
            'You join a fleet in one of two ways. If you have an invite code, enter it under '
                + 'Account in your Menu, on Join a Fleet. An emailed invite link lands you in the '
                + 'same place. Your operator can also add you to the fleet directly, in which case '
                + 'there is nothing to do: you will simply see that you are a fleet member. Fleets '
                + 'are run by the operator, so while you can join, leaving or switching fleets is '
                + 'handled on their end, not yours.',
            'Being in a fleet gives you two modes, and you choose which one each session runs '
                + 'under. In Fleet mode, the fleet\'s payment method covers the charge, not your card '
                + 'or wallet. In Public mode, you pay for yourself the usual way. You switch between '
                + 'them with a Fleet Mode toggle under Account, or the Fleet and Public pill in the '
                + 'top bar.',
            'The mode is set when a session starts, so flipping it applies to your next charge, '
                + 'not the one already running.',
            'One thing to know about points: a session the fleet pays for does not earn you '
                + 'personal points or badges, since you are not the one paying. It does not build '
                + 'your weekly streak, tick a friendship streak, or move you toward the next skin '
                + 'milestone either. Your referrals still count. When you charge in Public mode and '
                + 'pay for yourself, all of it counts again: points, badges, streaks, and skin '
                + 'progress.',
            'Your fleet driving has its own view. A My Fleet summary shows the sessions the fleet '
                + 'covered and an estimate of what that saved you.',
        ].join('\n\n'),
    },
    {
        slug: 'earning-as-a-host',
        title: 'Earning as a Host',
        summary: 'How you earn from a charger you host, where to see it, and the step to '
            + 'getting paid.',
        audience: 'host',
        related: ['getting-paid-stripe-express', 'software-pricing', 'starting-a-charge', 'sessions-and-receipts'],
        body: [
            'If you host a charger on EV Initiative, you can earn from it when paying guests charge '
                + 'there. Hosting turns your charger into something that pays you back.',
            'At a home, you set the price a guest pays per kWh at your charger. You keep 85% and '
                + 'EV Initiative takes 15%. Your household and the people on your free pass list '
                + 'always charge free. At a building, paid guest charging at a resident\'s charger '
                + 'isn\'t available yet. You can still let a neighbor or a visitor charge free by '
                + 'adding them to your free pass list.',
            'You earn on completed sessions, and only once the money clears. A session still '
                + 'settling does not show in your total yet.',
            'Everything is in one place. Open the Menu, go to My Chargers, choose a charger, and '
                + 'open its Earnings. You will see your totals over time, your most recent payout and '
                + 'when it landed, a recent trend, and how many sessions you have hosted. This is '
                + 'also where you set up getting paid.',
            'Receiving the money runs through a payout account. See Getting Paid with Stripe '
                + 'Express for how to connect one and when payouts land.',
        ].join('\n\n'),
    },
    {
        slug: 'getting-paid-stripe-express',
        title: 'Getting Paid with Stripe Express',
        summary: 'Connecting your Stripe Express payout account, and how and when your earnings '
            + 'reach your bank.',
        audience: 'host',
        related: ['earning-as-a-host', 'payment-and-wallet'],
        body: [
            'Your hosting earnings reach your bank through Stripe Express, a payout account that '
                + 'handles the money side. You connect it once, and from then on your earnings flow '
                + 'to you on their own.',
            'You start the connection from your earnings. Open My Chargers, pick a charger, open '
                + 'its Earnings, and choose Set up payouts. First choose your bank account country, '
                + 'United States or Canada, and confirm it. Stripe can\'t change it later, so choose '
                + 'the country where you live and bank. The app then creates your Stripe Express '
                + 'account and hands you off to Stripe to finish.',
            'Guests can\'t pay to charge at your charger until this is done. Turn on earning and '
                + 'Open to guests stay off until Stripe confirms your account.',
            'Stripe asks for what it needs to pay you and to meet the rules around moving money: '
                + 'your name, your date of birth, an ID, your address, and the bank account where '
                + 'you want your earnings to land. This part happens on Stripe\'s own secure pages, '
                + 'not in the app.',
            'When you are done, Stripe sends you back, and the app checks that everything is in '
                + 'order. If something is still missing, you can pick up where you left off. Once you '
                + 'are set, your charger is ready to pay out.',
            'From there it runs itself. After a guest finishes a session, your share moves to '
                + 'your Stripe account within seconds. Stripe then sends your balance to your bank on '
                + 'your account\'s payout schedule, which you can see in your Stripe dashboard. New '
                + 'accounts usually see a short hold on the very first payout.',
            'You can check on it any time. The Open Stripe dashboard link on your Earnings page '
                + 'takes you to Stripe, where your balance, your transfers, and your payout schedule '
                + 'all live.',
        ].join('\n\n'),
    },
    {
        // STATES ONLY WHAT THE BILLING CODE ENFORCES. The per-charger fee is
        // the tenant subscription quantity sync; the residential exemption is
        // provisionHome creating no subscription; cadence is the billing_cadence
        // enum; period adjustment is the quantity re-sync on charger changes;
        // cancellation is cancel_at_period_end (billing/subscriptions.ts:47);
        // the fleet base+per-charger split and the DLM per-managed-charger
        // add-on are the fleet/dlm subscription item shapes.
        //
        // Tier 2 (2026-09-26): the 85/15 split is the 1500 bps floor every
        // Session creator pins (@evi/billing pinAdminFeeBps) and settlement
        // honours (settleAdminFeeBps). The connection fee is
        // Charger.connection_fee_cents (DEFAULT_CONNECTION_FEE_CENTS on every
        // create path, staff-set), pinned at start only when the billing
        // tariff can bill (resolveConnectionFeeAtStart), and carved out whole
        // for EVI in calculatePayout. The Sales Partner share is computed on
        // the percentage part alone (agentCommissionCents), never on the fee.
        //
        // Deliberately absent until they exist in code or in Stripe:
        //   - Dollar amounts. The $8/$18/$20 in tenant-prices.ts and
        //     fleet-prices.ts are comments, and a comment is not a price.
        //   - Any free-charger-bills-at-a-different-rate rule. No billing path
        //     reads a tariff or a free/paid state (verified 2026-07-31).
        //   - "RFID credentials are never charged for". NOT TRUE as written:
        //     rfid-cards.ts has per-channel registration pricing with a real
        //     charge flow, and the store sells RFID two-packs at $50.
        // Do not add any of these without confirming against Stripe or code.
        slug: 'software-pricing',
        title: 'What the Software Costs',
        summary: 'What a site pays per charger, what a fleet pays, what a residential host '
            + 'pays, and what a driver pays.',
        audience: 'host',
        related: ['earning-as-a-host', 'fleets', 'my-home'],
        // Prices confirmed against the Stripe dashboard and published on
        // evinitiative.com, so they are public figures, not internal ones.
        //
        // Written as SEPARATE FACTS, one per paragraph, on purpose: this body
        // lands verbatim in EVII's system prompt, and a price TABLE invites the
        // model to recite the whole thing at someone who asked one question.
        // The "answer only what was asked" RULE lives in the prompts, not here,
        // because this text is also the public Help page and gets translated —
        // an instruction addressed to the assistant does not belong in either.
        //
        // DELIBERATELY ABSENT: the claim that an operator's free charger bills
        // at the fleet rate. That rule does not exist in code — POST
        // /tariffs/:id/assign imports no billing module at all, and a charger's
        // price follows only the document it is denormalised onto. EVII must
        // not state a billing rule the system will not apply.
        body: [
            'We charge for the software that runs your chargers. Not for the electricity, and '
                + 'not for the hardware.',
            'If you run a site as an operator or property manager, the software is $8 per '
                + 'charger per month, or $80 per charger for a year.',
            'A fleet account is $20 a month as a base, plus $18 per charger. For a year it is '
                + '$200 base plus $180 per charger. A fleet account pays for the charging its '
                + 'drivers do wherever they do it, including at a driver\'s home charger.',
            'Dynamic load management is $4 per managed charger per month, or $40 for a year. '
                + 'You add it to an existing subscription when a site\'s electrical service '
                + 'cannot run every charger at full power at once.',
            'Every annual plan is priced at ten months, so paying for the year gives you two '
                + 'months free.',
            'If you host at home, you pay nothing. No subscription, no per-charger fee, no '
                + 'listing fee. When a guest charges at your charger, you keep 85% of what they '
                + 'pay for charging and EVI keeps 15% as its platform fee.',
            'EVI also charges a connection fee. EVI sets it for each charger, and a new charger '
                + 'starts at $1. It is charged once, on a paid session, and EVI keeps all of it. It '
                + 'never comes out of the host\'s 85%. A free session never carries it.',
            'The app, the account, and support are free for drivers.',
            'On a paid session, a driver may also pay EVI\'s connection fee on top of the price '
                + 'for charging. When a driver starts in the app, the fee shows before they start, '
                + 'and it is on their receipt. A free charger stays free, with no connection fee.',
            'A driver can also be charged for an RFID credential. A new physical card is $50. '
                + 'Registering a fob you already own is $25. Fleet keyfob registration is $15 and '
                + 'the operator pays it.',
            'Sales Partner commission is 3.00% of session revenue for a Standard partner, and '
                + '3.50% to 5.00% for a Golden partner. The rate is stamped on each charger when it is '
                + 'deployed and stays with that charger. It '
                + 'comes out of EVI\'s 15%, never out of the host\'s 85%, and never out of the '
                + 'connection fee.',
            'These are the published list prices. What an individual account pays is whatever '
                + 'that account\'s plan says, so check the billing page in the platform for your '
                + 'own bill.',
        ].join('\n\n'),
    },
];
/** Look up a manual page by its stable slug. */
export function manualPageBySlug(slug) {
    return MANUAL_PAGES.find((p) => p.slug === slug);
}
/** Resolve a spoken/typed section name to its page (case-insensitive, exact
 *  title or slug). The EVII deep-link hook: she names a section, this finds it. */
export function manualPageByName(name) {
    const n = name.trim().toLowerCase();
    return MANUAL_PAGES.find((p) => p.title.toLowerCase() === n || p.slug === n);
}
/** The route for a manual page slug. EVII navigates here once wired. */
export function manualRouteForSlug(slug) {
    return `/help/${slug}`;
}
// ── Localization: per-locale text overlays ────────────────────────────
// Text only (title/summary/body); slug, audience and related stay
// single-source above. Per-key English fallback. Full contract in
// ./locales/registry.ts. EVII's prompt (buildAppKnowledgeBlock) stays
// English on purpose: the reply-language instruction arrives after the
// cache breakpoint, and locale-keying the block would fork the prompt
// cache eleven ways for no gain.
/** Overlay for a UI locale ('pt-BR' resolves as 'pt'); undefined for
 *  unknown locales, which callers treat as plain English. */
function overlayFor(locale) {
    const base = String(locale ?? '').toLowerCase().split('-')[0] ?? '';
    return CONTENT_OVERLAYS[base];
}
function localizePage(page, o) {
    if (!o)
        return page;
    // `||` not `??`: an empty translated string falls back to English
    // too — partial translations degrade per key, never to a blank page.
    return {
        ...page,
        title: o.title || page.title,
        summary: o.summary || page.summary,
        body: o.body || page.body,
    };
}
/** MANUAL_PAGES with <locale>'s text overlay applied per key. */
export function manualPagesForLocale(locale) {
    const ov = overlayFor(locale)?.manual;
    return MANUAL_PAGES.map((p) => p.slug === 'idle-fees'
        ? { ...localizePage(p, ov?.[p.slug]), body: idleManualBody(locale ?? 'en') } : localizePage(p, ov?.[p.slug]));
}
/** manualPageBySlug with <locale>'s text overlay applied per key. */
export function manualPageBySlugForLocale(slug, locale) {
    const page = manualPageBySlug(slug);
    if (!page)
        return undefined;
    const localized = localizePage(page, overlayFor(locale)?.manual[page.slug]);
    return slug === 'idle-fees' ? { ...localized, body: idleManualBody(locale ?? 'en') } : localized;
}
/** The manual section the EVII voice sample is spoken from. */
export const VOICE_SAMPLE_SLUG = 'ask-evie';
/**
 * The line the voice picker previews, in <locale>: the OPENING SENTENCE of
 * the Ask EVII manual body ("EVII is the assistant built into the app.").
 *
 * Derived, never duplicated. The sample is a view of the manual, so a
 * reword of that first sentence changes the line everywhere by
 * construction — there is no second copy in a locale file to fall out of
 * sync. scripts/generate-voice-samples.ts synthesises from this, and the
 * committed clips carry the exact text they were spoken from so a drift
 * test can fail CI when the manual moves ahead of the audio.
 *
 * Sentence split handles Latin terminators, the CJK full stop (。！？) used
 * by ja/zh, and the ARMENIAN full stop `։` (U+0589) — which is NOT the
 * ASCII colon and is easy to miss: without it hy returned the entire
 * 672-character body as "one sentence". Harmless while Armenian is
 * text-only (no Polly voice, so no clip), but it would have fed a whole
 * page to the synthesiser the day a voice appears.
 */
export function voiceSampleLine(locale) {
    const page = manualPageBySlugForLocale(VOICE_SAMPLE_SLUG, locale);
    const body = (page?.body ?? '').trim();
    if (!body)
        return '';
    const first = body.split(/(?<=[.!?。！？։])/)[0] ?? body;
    return first.trim();
}
/**
 * Changelog overlay keys for an entry list: the ISO date, with '#N'
 * appended for the second and later entries sharing a date, in array
 * order. Dates are NOT unique (2026-07-03 already appears twice), so
 * same-date array order is part of the overlay identity — append new
 * entries at the top and never reorder a same-date group. Exported
 * for the en-overlay generator so both sides compute identical keys.
 */
export function changelogOverlayKeys(entries) {
    const seen = new Map();
    return entries.map((e) => {
        const n = (seen.get(e.date) ?? 0) + 1;
        seen.set(e.date, n);
        return n === 1 ? e.date : `${e.date}#${n}`;
    });
}
/** CHANGELOG_ENTRIES with <locale>'s text overlay applied per key. */
export function changelogForLocale(locale) {
    const ov = overlayFor(locale)?.changelog;
    if (!ov)
        return CHANGELOG_ENTRIES;
    const keys = changelogOverlayKeys(CHANGELOG_ENTRIES);
    return CHANGELOG_ENTRIES.map((e, i) => {
        const o = ov[keys[i] ?? ''];
        if (!o)
            return e;
        return { ...e, title: o.title || e.title, body: o.body || e.body };
    });
}
/** All manual chunks, in deterministic order (wallbox first, then pedestal,
 *  page order within each). The api builds a keyword index over these once at
 *  module load. */
export const MANUAL_CHUNKS = manualChunksRaw.chunks;
//# sourceMappingURL=index.js.map